import assert from "assert";
import { CallOutError } from "components/CallOutError/CallOutError";
import { useWorkflowEditorContext } from "components/WorkflowEditor/WorkflowEditorProvider/WorkflowEditorProvider";
import { StepParamMapping } from "pages/gdt/modals/ModalCreateColumn/steps/StepParamMapping";
import { ToolSpec } from "types/json-schemas/type_guards";
import { isDefined } from "utils/isDefined";

export const WorkflowEditorPageMapSources = () => {
  const { params, stepNameByParamKey } = useWorkflowEditorContext((s) => {
    const graph = s.workflowGraph;
    assert(isDefined(graph), "Expected workflow graph to be defined");

    const stepNameByParamKey: Record<string, string> = {};
    const tableInfoOverrides = s.tableInfoOverrides;

    // Workflow parameters are the set of all tool spec params across their steps. We key them by
    // their step ID and their param key so that duplicate keys don't cause a conflict.
    const inputParams = graph.inputs
      .filter((input) => !input.intermediate)
      .map<ToolSpec["params"][number]>((input) => {
        stepNameByParamKey[input.id] = tableInfoOverrides[input.table_id].name;

        return { ...input.param, key: input.id };
      });

    const parameterParams = graph.parameters
      .map((parameter) => {
        stepNameByParamKey[parameter.id] =
          tableInfoOverrides[parameter.table_id].name;

        return { ...parameter.param, key: parameter.id };
      })
      .filter(isDefined);

    return {
      params: [...inputParams, ...parameterParams],
      stepNameByParamKey,
    };
  });

  const excludedTableKeys = useWorkflowEditorContext((s) =>
    s.tableInfo.map((t) => t.tableKey),
  );

  const sourceMap = useWorkflowEditorContext((s) => s.sourceMap);
  const setParamMap = useWorkflowEditorContext((s) => s.setParamMap);
  const setSourceTableIds = useWorkflowEditorContext(
    (s) => s.setSourceTableIds,
  );

  if (params === undefined) {
    return <CallOutError />;
  }

  return (
    <StepParamMapping
      dataTableId={undefined}
      setSourceTableIds={setSourceTableIds}
      excludedTableKeys={excludedTableKeys}
      sourceTableIds={sourceMap.sourceTableIds}
      toolSpec={{ params }}
      paramMap={{ value: sourceMap.map, isDirty: true }}
      setParamMap={(paramMap) => {
        setParamMap(() => paramMap);
      }}
      paramColumns={[
        {
          name: "Workflow Step",
          render: (param) => stepNameByParamKey[param.key],
        },
      ]}
    />
  );
};
